import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useSnackbar } from "notistack";

/**
 * GoCardless Callback Handler
 *
 * This handles the redirect from GoCardless and passes data via URL parameters
 * back to the list page, which will reopen the dialog.
 */
export default function GoCardlessCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing",
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get redirect flow ID from GoCardless callback URL
      const redirectFlowId = searchParams.get("redirect_flow_id");

      // Get direct debit ID from session storage (set before redirect)
      const directDebitId = sessionStorage.getItem("directDebitId");

      if (!redirectFlowId) {
        throw new Error("Redirect flow ID not found in URL");
      }

      if (!directDebitId) {
        throw new Error(
          "Direct Debit ID not found in session storage. It may have been cleared.",
        );
      }

      setStatus("success");

      // IMPORTANT: Pass data via URL parameters instead of session storage
      // This is more reliable as session storage can be cleared
      const returnUrl = `/debits/list?reopen=true&step=2&ddId=${directDebitId}&flowId=${redirectFlowId}`;

      // Navigate back with URL parameters
      setTimeout(() => {
        navigate(returnUrl);
      }, 1500);
    } catch (error: any) {
      console.error("Callback error:", error);
      setStatus("error");
      setErrorMessage(
        error.message || "An error occurred processing the callback",
      );
      enqueueSnackbar("Failed to process bank setup callback", {
        variant: "error",
      });

      // Still navigate back, but without the success parameters
      setTimeout(() => {
        navigate("/debits/list");
      }, 3000);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}>
      {status === "processing" && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Processing bank account setup...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we verify your bank details
          </Typography>
        </>
      )}

      {status === "success" && (
        <>
          <Alert severity="success" sx={{ mb: 3, maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Bank Account Verified! ✓
            </Typography>
            <Typography variant="body2">
              Redirecting you back to complete the payment setup...
            </Typography>
          </Alert>
        </>
      )}

      {status === "error" && (
        <>
          <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Setup Failed
            </Typography>
            <Typography variant="body2">{errorMessage}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Redirecting back to try again...
            </Typography>
          </Alert>
        </>
      )}
    </Box>
  );
}
